import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Virtuoso } from 'react-virtuoso';
import CheveronIcon from '@/assets/icon-cheveron.svg?react';

export interface ProfileNode {
  value: string;
  label: string;
  children?: ProfileNode[];
}

type TreeDropdownProps = {
  data: ProfileNode[];
  selectedId?: string;
  placeholder?: string;
  onSelect: (node: ProfileNode) => void;
  onDropdownOpen?: any;
  onClear?: () => void;
  disabled?: boolean;
};

type FlatNode = {
  id: string;
  label: string;
  level: number;
  hasChildren: boolean;
};

const makeNodeMap = (nodes: ProfileNode[]): Map<string, ProfileNode> => {
  const map = new Map<string, ProfileNode>();

  const walk = (arr: ProfileNode[] = []) => {
    for (const n of arr) {
      map.set(n.value, n);
      map.set(n.label, n);
      if (n.children && n.children.length) {
        walk(n.children);
      }
    }
  };

  walk(nodes);
  return map;
};

const buildVisibleList = (
  nodes: ProfileNode[] = [],
  expanded: Set<string>,
  level = 0,
  acc: FlatNode[] = []
): FlatNode[] => {
  for (const n of nodes) {
    acc.push({
      id: n.value,
      label: n.label,
      level,
      hasChildren: !!n.children && n.children.length > 0,
    });

    if (n.children && n.children.length && expanded.has(n.value)) {
      buildVisibleList(n.children, expanded, level + 1, acc);
    }
  }
  return acc;
};

export const TreeDropdown = ({
  data,
  selectedId,
  placeholder = 'Select…',
  onSelect,
  onDropdownOpen,
  onClear,
  disabled = false,
}: TreeDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [listHeight, setListHeight] = useState<number>(250);
  const virtuosoRef = useRef<any>(null);

  const nodeMap = useMemo(() => makeNodeMap(data), [data]);

  const visibleNodes = useMemo(() => buildVisibleList(data, expanded), [data, expanded]);

  // const selectedNode = selectedId ? (nodeMap.get(selectedId) ?? null) : null;
  const selectedNode = useMemo(() => {
    if (!selectedId) return null;

    let node = nodeMap.get(selectedId);

    if (!node) {
      node = nodeMap.get(selectedId);
    }

    // If still not found and contains |, try the last segment
    if (!node && selectedId.includes('|')) {
      const lastPart = selectedId.split('|').pop()?.trim();
      if (lastPart) {
        node = nodeMap.get(lastPart);
      }
    }

    return node ?? null;
  }, [selectedId, nodeMap]);

  useEffect(() => {
    if (open && visibleNodes.length > 0) {
      const estimatedRowHeight = 40;
      const contentHeight = visibleNodes.length * estimatedRowHeight;

      const newHeight = Math.min(Math.max(contentHeight, estimatedRowHeight), 250);
      setListHeight(newHeight);
    }
  }, [open, visibleNodes]);

  useEffect(() => {
    if (open && onDropdownOpen) {
      onDropdownOpen();
    }
  }, [open, onDropdownOpen]);

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      const node = nodeMap.get(id);
      if (!node) return;
      onSelect(node);
      setOpen(false);
    },
    [nodeMap, onSelect]
  );

  return (
    <div className="tree-select-wrapper">
      <Dropdown className="w-100" show={open} onToggle={(v) => setOpen(!!v)}>
        <Dropdown.Toggle
          id="tree-dropdown-toggle"
          className="form-select d-flex justify-content-between align-items-center w-100"
          variant="outline-secondary"
          onClick={() => {
            setOpen((v) => !v);
          }}
          disabled={disabled}
        >
          <span className={selectedNode ? '' : 'empty'}>
            {selectedNode ? selectedNode.label : placeholder}
          </span>
        </Dropdown.Toggle>

        {open && (
          <Dropdown.Menu className="tree-dropdown-menu" align="start">
            {selectedNode && (
              <div className="d-flex justify-content-end align-items-center px-3 border-bottom-control bg-transparent">
                <button
                  type="button"
                  className="clear-selection btn btn-link p-0 text-decoration-none small "
                  onClick={() => {
                    onClear?.();
                  }}
                >
                  Clear Selection
                </button>
              </div>
            )}

            <div className="tree">
              <Virtuoso
                ref={virtuosoRef}
                id="tree-dropdown"
                style={{
                  height: listHeight,
                  transition: 'height 0.2s ease-in-out',
                }}
                totalCount={visibleNodes.length}
                itemContent={(index) => {
                  const item = visibleNodes[index];
                  const isExpanded = expanded.has(item.id);
                  const isSelected = selectedId === item.id;

                  return (
                    <div
                      className={`tree-node ${isSelected ? 'tree-node--selected fw-semibold' : ''}`}
                      style={
                        {
                          '--level': item.level,
                          '--max-indent': 200,
                        } as React.CSSProperties
                      }
                    >
                      {item.hasChildren ? (
                        <button
                          type="button"
                          className="tree-node__toggle"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(item.id);
                          }}
                        >
                          <CheveronIcon
                            className={
                              isExpanded
                                ? 'tree-node__chevron tree-node__chevron--open'
                                : 'tree-node__chevron'
                            }
                          />
                        </button>
                      ) : (
                        <span className="tree-node__toggle tree-node__toggle--spacer" />
                      )}

                      <div className="tree-node__row" onClick={() => handleSelect(item.id)}>
                        <span className="tree-node__label">{item.label}</span>
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          </Dropdown.Menu>
        )}
      </Dropdown>
    </div>
  );
};
